import React, { useEffect, useState, useMemo, useRef } from "react";
import { FormService, FormField } from "@shared/services/FormServices";
import { SharedFormArray } from "@shared/components/SharedFormArray";
import { SegmentedControl } from "@shared/components/SegmentedControl";
import { Button } from "@shared/ui/button";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useWatch } from "react-hook-form";

import { useParams } from "react-router-dom";
import { useGetPlanDetail, useUtilityServices, useUpdatePlan, useGetPlanDetailByTab } from "../../../hooks";
import { useQueries } from "@tanstack/react-query";
import { PlanApi } from "../../../api";

interface PenaltiesProps {
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const getSelectedServices = (val: any): string[] => {
  if (Array.isArray(val)) {
    return val
      .map((s: any) => (typeof s === "object" && s !== null ? (s.value ?? s.label ?? String(s)) : String(s)))
      .flatMap((s: string) => s.split(","))
      .map((s: string) => s.trim())
      .filter(Boolean);
  } else if (typeof val === "string") {
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  } else if (val !== null && val !== undefined && val !== "") {
    return [String(val).trim()];
  }
  return [];
};

const getSelectedValues = (val: any): string[] => {
  if (Array.isArray(val)) {
    return val
      .map((s: any) => (typeof s === "object" && s !== null ? (s.value ?? s.label ?? s.key ?? String(s)) : String(s)))
      .flatMap((s: string) => s.split(","))
      .map((s: string) => s.trim())
      .filter(Boolean);
  } else if (typeof val === "string") {
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  } else if (val !== null && val !== undefined && val !== "") {
    return [String(val).trim()];
  }
  return [];
};

export const Penalties: React.FC<PenaltiesProps> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
}) => {
  const { action, planId } = useParams();
  const [hasInitialData] = useState(() => Object.keys(data || {}).length > 0);
  const queryPlanId = hasInitialData ? undefined : planId;
  const { data: planDetail } = useGetPlanDetailByTab(queryPlanId, "penalties");
  const { mutateAsync: updatePlan, isPending } = useUpdatePlan();
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const [mode, setMode] = useState('common');
  const [enablePenalties, setEnablePenalties] = useState(true);

  const penaltiesForm = FormService.createForm({
    fields: [],
    defaultValues: data || { penalties: [{}] },
    mode: "onChange",
  });

  const formPenalties = useWatch({
    control: penaltiesForm.control,
    name: "penalties"
  }) || [];

  const prevServicesRef = React.useRef<string[]>([]);
  const selectedUtilityServices = useMemo(() => {
    const allServices = new Set<string>();
    formPenalties.forEach((rule: any) => {
      const services = getSelectedServices(rule?.utilityService);
      services.forEach((s: string) => allServices.add(s));
    });

    const newServices = Array.from(allServices).sort();
    if (prevServicesRef.current.length !== newServices.length || !prevServicesRef.current.every((v, i) => v === newServices[i])) {
      prevServicesRef.current = newServices;
    }
    return prevServicesRef.current;
  }, [formPenalties]);

  const { data: servicesResult } = useUtilityServices();
  const serviceOptions = useMemo(() => {
    const responseData = servicesResult?.result || servicesResult;
    if (!Array.isArray(responseData)) return [];
    return responseData
      .filter((service: any) => service.isActive === true)
      .map((service: any) => ({
        label: service.name,
        value: service.name,
      }));
  }, [servicesResult]);

  // Make individual API calls per unique service so we can map service → parameters
  const parameterQueries = useQueries({
    queries: selectedUtilityServices.map(service => ({
      queryKey: ["planParameter", remoteUtilityId, service],
      queryFn: () => PlanApi.getParameter(remoteUtilityId, service),
      enabled: !!remoteUtilityId && !!service,
      staleTime: Infinity,
    }))
  });

  // Build service → parameters[] map
  const parameterQueryData = parameterQueries.map(q => q.data);
  const parametersByService = useMemo(() => {
    const map = new Map<string, any[]>();
    selectedUtilityServices.forEach((service, idx) => {
      const data = parameterQueryData[idx];
      const list = data?.result || data;
      map.set(service, Array.isArray(list) ? list : []);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUtilityServices, JSON.stringify(parameterQueryData)]);

  // Flat list of all parameters (for edit-mode population / lookups)
  const allParametersList = useMemo(() => {
    const seen = new Set<string>();
    const result: any[] = [];
    parametersByService.forEach(params => {
      params.forEach(p => {
        const id = String(p.id);
        if (!seen.has(id)) {
          seen.add(id);
          result.push(p);
        }
      });
    });
    return result;
  }, [parametersByService]);

  const parametersOptions = useMemo(() => {
    return allParametersList.map((p: any) => ({
      label: p.name,
      value: String(p.id),
      ...(p.allowed_values && p.allowed_values.length > 0 && { options: p.allowed_values.map((v: any) => ({ label: v, value: v })) })
    }));
  }, [allParametersList]);

  // Per-row getOptions: filter parameters by THIS row's selected utility services
  const getParameterOptionsForRow = React.useCallback((rowData: any) => {
    const rowServices = getSelectedServices(rowData?.utilityService);
    if (rowServices.length === 0) return [];
    const seen = new Set<string>();
    const result: { label: string; value: string }[] = [];
    rowServices.forEach(service => {
      const params = parametersByService.get(service) || [];
      params.forEach((p: any) => {
        const id = String(p.id);
        if (!seen.has(id)) {
          seen.add(id);
          result.push({ label: p.name, value: id });
        }
      });
    });
    return result;
  }, [parametersByService]);

  // Per-row getOptions: get allowed values for THIS row's selected parameter
  const getValueOptionsForRow = React.useCallback(
    (rowData: any) => {
      const selectedParamId = rowData?.parameter;
      if (!selectedParamId) return [];
      const sParamId = String(selectedParamId).toLowerCase();
      const param = allParametersList.find((p: any) => {
        return (
          String(p.id ?? "").toLowerCase() === sParamId ||
          String(p.parameter_id ?? "").toLowerCase() === sParamId ||
          String(p.name ?? "").toLowerCase() === sParamId ||
          String(p.code ?? "").toLowerCase() === sParamId ||
          String(p.key ?? "").toLowerCase() === sParamId
        );
      });
      if (!param) return [];
      const allowedValues =
        param?.allowedValues ||
        param?.allowed_values ||
        param?.allowedValuesOptions ||
        param?.values ||
        [];
      if (Array.isArray(allowedValues) && allowedValues.length > 0) {
        return allowedValues.map((val: any) => {
          if (typeof val === "object" && val !== null) {
            const label =
              val?.display ||
              val?.label ||
              val?.name ||
              val?.key ||
              String(val?.value || "");
            const value = String(val?.key ?? val?.value ?? val?.id ?? label);
            return { label, value };
          }
          return { label: String(val), value: String(val) };
        });
      }
      return [];
    },
    [allParametersList]
  );

  // Common Penalty Fields
  const commonPenaltyFields: FormField[] = useMemo(() => [
    { name: "penaltyName", label: "Penalty Name", type: "text", placeholder: "e.g. Late Payment Fee" },
    { name: "type", label: "Type", type: "select", options: [{ label: "Flat " + currency, value: "flat" }, { label: "Percent %", value: "percent" }] },
    { name: "amount", label: "Amount", type: "number", placeholder: "e.g. 100" },
    { name: "grace", label: "Grace (days)", type: "number", placeholder: "e.g. 10" },
    {
      name: "businessDays", label: "Business days", type: "select", options: [
        { label: "Calendar Days", value: "calendar_days" },
        { label: "Mon - Fri", value: "mon_fri" },
        { label: "Mon - Sat", value: "mon_sat" },

      ]
    }
  ], [currency]);

  // Parameter-wise Penalty Fields
  const parameterWisePenaltyFields: FormField[] = useMemo(() => [
    { name: "utilityService", label: "Utility Service", type: "multi-select", options: serviceOptions, className: "md:col-span-3" },
    { name: "parameter", label: "Parameter", type: "select", getOptions: getParameterOptionsForRow, className: "md:col-span-3" },
    { name: "value", label: "Value", type: "multi-select", getOptions: getValueOptionsForRow, className: "md:col-span-3" },
    { name: "penaltyName", label: "Penalty Name", type: "text", placeholder: "e.g. Late Payment Fee", className: "md:col-span-3" },
    { name: "chargeType", label: "Charge Type", type: "select", options: [{ label: "Flat " + currency, value: "flat" }, { label: "Percent %", value: "percent" }], className: "md:col-span-3" },
    { name: "amount", label: "Amount", type: "number", placeholder: "e.g. 100", className: "md:col-span-3" },
    { name: "grace", label: "Grace (days)", type: "number", placeholder: "e.g. 10", className: "md:col-span-3" },
    {
      name: "businessDays", label: "Business days", type: "select", options: [
        { label: "Calendar Days", value: "calendar_days" },
        { label: "Mon - Fri", value: "mon_fri" },
        { label: "Mon - Sat", value: "mon_sat" }
      ], className: "md:col-span-3"
    }
  ], [serviceOptions, getParameterOptionsForRow, currency, getValueOptionsForRow]);

  const isPopulated = React.useRef(false);
  const isLocalStorageRestored = React.useRef(false);

  useEffect(() => {
    if (hasInitialData && data && !isLocalStorageRestored.current) {
      if (data.mode) setMode(data.mode);
      if (data.enablePenalties !== undefined) setEnablePenalties(data.enablePenalties);
      if (data.penalties && Array.isArray(data.penalties)) {
        const normalizedPenalties = data.penalties.map((rule: any) => ({
          ...rule,
          utilityService: getSelectedServices(rule.utilityService || rule.utilityServices || rule.utility_services),
          value: getSelectedValues(rule.value || rule.parameterValues || rule.parameter_values),
        }));
        FormService.populateForm(penaltiesForm, { ...data, penalties: normalizedPenalties }, [
          { name: 'penalties', type: 'form-array' }
        ] as any[]);
      }
      isLocalStorageRestored.current = true;
    }
  }, [hasInitialData, data, penaltiesForm]);

  useEffect(() => {
    isPopulated.current = false;
  }, [planId]);

  useEffect(() => {
    if (hasInitialData) return;
    if (planDetail?.result && planId && planId !== "create" && !isPopulated.current) {
      const res = planDetail.result;

      if (res.enabled !== undefined) {
        setEnablePenalties(res.enabled);
      }

      const rulesGrouped = res.rulesGrouped || res.rules_grouped || [];
      // The penalties_config API returns a flat `rules` array - support that too
      const flatRules: any[] = res.rules || [];
      const mappedData: any = {};

      if (rulesGrouped.length > 0) {
        const firstGroup = rulesGrouped[0];
        const firstRule = (firstGroup.rules || [])[0];
        const ruleMode = firstRule?.mode?.toLowerCase()?.replace('-', '_');

        if (firstRule && ruleMode === 'common') {
          setMode("common");

          const allRules = rulesGrouped.flatMap((group: any) =>
            (group.rules || []).map((rule: any) => ({
              penaltyName: rule.penaltyName || rule.penalty_name || "",
              type: rule.chargeType || rule.charge_type || "flat",
              amount: rule.amount,
              grace: rule.graceDays || rule.grace_days || 0
            }))
          );
          mappedData.penalties = allRules;

        } else if (firstRule && (ruleMode === 'parameter_wise' || ruleMode === 'parameter-wise')) {
          setMode("parameter-wise");
          const allRules = rulesGrouped.flatMap((group: any) => {
            return (group.rules || []).map((rule: any) => ({
              parameter: String(group.parameterId || group.parameter_id || rule.parameterId || rule.parameter_id || rule.parameter || ""),
              value: getSelectedValues(rule.parameterValues || rule.parameter_values || rule.value),
              utilityService: getSelectedServices(rule.utilityServices || rule.utility_services || rule.utilityService),
              penaltyName: rule.penaltyName || rule.penalty_name || "",
              chargeType: rule.chargeType || rule.charge_type || "flat",
              amount: rule.amount,
              grace: rule.graceDays || rule.grace_days || 0
            }));
          });
          mappedData.penalties = allRules;
        }
      } else if (flatRules.length > 0) {
        // flat shape (penalties_config.rules)
        const firstRule = flatRules[0];
        const ruleMode = firstRule?.mode?.toLowerCase()?.replace('-', '_');

        if (ruleMode === 'common' || !ruleMode) {
          setMode("common");
          const allRules = flatRules.map((rule: any) => ({
            penaltyName: rule.penaltyName || rule.penalty_name || "",
            type: rule.chargeType || rule.charge_type || "flat",
            amount: rule.amount,
            grace: rule.graceDays || rule.grace_days || 0
          }));
          mappedData.penalties = allRules;
        } else if (ruleMode === 'parameter_wise' || ruleMode === 'parameter-wise') {
          setMode("parameter-wise");
          const allRules = flatRules.map((rule: any) => ({
            parameter: String(rule.parameterId || rule.parameter_id || rule.parameter || ""),
            value: getSelectedValues(rule.parameterValues || rule.parameter_values || rule.value),
            utilityService: getSelectedServices(rule.utilityServices || rule.utility_services || rule.utilityService),
            penaltyName: rule.penaltyName || rule.penalty_name || "",
            chargeType: rule.chargeType || rule.charge_type || "flat",
            amount: rule.amount,
            grace: rule.graceDays || rule.grace_days || 0
          }));
          mappedData.penalties = allRules;
        }
      }

      FormService.populateForm(penaltiesForm, mappedData, [
        { name: 'penalties', type: 'form-array' }
      ] as any[]);

      isPopulated.current = true;
    }
  }, [planDetail, planId, penaltiesForm, allParametersList]);

  const onDataChangeRef = useRef(onDataChange);
  const dataRef = useRef(data);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
    dataRef.current = data;
  }, [onDataChange, data]);

  useEffect(() => {
    if (!onDataChangeRef.current) return;
    onDataChangeRef.current({ ...dataRef.current, ...penaltiesForm.getValues(), mode, enablePenalties });
    const subscription = penaltiesForm.watch((value) => {
      onDataChangeRef.current?.({ ...dataRef.current, ...value, mode, enablePenalties });
    });
    return () => subscription.unsubscribe();
  }, [penaltiesForm, mode, enablePenalties]);

  const penaltiesCache = React.useRef<{ common: any[], 'parameter-wise': any[] }>({ common: [], 'parameter-wise': [] });

  const handleModeChange = (val: string) => {
    penaltiesCache.current[mode as 'common' | 'parameter-wise'] = penaltiesForm.getValues("penalties") || [];
    setMode(val);
    penaltiesForm.setValue("penalties", penaltiesCache.current[val as 'common' | 'parameter-wise'] || []);
  };

  const validateAndNext = async () => {
    const isValid = enablePenalties ? await penaltiesForm.trigger() : true;
    if (isValid) {
      if (planId && planId !== "create") {
        try {
          const formData = penaltiesForm.getValues();
          const rules: any[] = [];



          if (enablePenalties) {
            const formPenaltiesArray = formData.penalties || [];
            formPenaltiesArray.forEach((item: any, index: number) => {
              if (mode === "common") {
                rules.push({
                  id: `pen-00${index + 1}`,
                  mode: "common",
                  penalty_name: item.penaltyName,
                  charge_type: item.type,
                  amount: Number(item.amount || 0),
                  grace_days: Number(item.grace || 0)
                });
              } else {
                const utility_services = getSelectedServices(item.utilityService);
                const paramObj = allParametersList.find((p: any) => String(p.id) === String(item.parameter));
                const paramCode = paramObj?.code || item.code || item.parameter_code || "";
                rules.push({
                  id: `pen-00${index + 1}`,
                  mode: "parameter_wise",
                  utility_services,
                  parameter_id: item.parameter,
                  parameter_code: paramCode,
                  parameter_values: getSelectedValues(item.value || item.parameter_values || item.parameterValues),
                  penalty_name: item.penaltyName,
                  charge_type: item.chargeType,
                  amount: Number(item.amount || 0),
                  grace_days: Number(item.grace || 0),
                  priority: index + 1
                });
              }
            });
          }

          const payload = {
            current_step: 6,
            remote_utility_id: remoteUtilityId,
            penalties_config: {
              enabled: enablePenalties,
              rules: rules
            }
          };

          if (action !== 'edit') {
            await updatePlan({ planId, payload });
          }
          if (onNext) onNext();
        } catch (error) {
          console.error("Failed to update penalties config:", error);
        }
      } else {
        if (onNext) onNext();
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Penalty Rules</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            Configure penalties for this plan — late fees, reconnection charges, returned-payment fees.
            In Common mode, set flat fees for all consumers. In Parameter-wise mode, vary fees by customer class, meter size, or any attribute.
          </p>
        </div>
        <SegmentedControl
          options={[
            { label: 'Common', value: 'common' },
            { label: 'Parameter-wise', value: 'parameter-wise' }
          ]}
          value={mode}
          onChange={handleModeChange}
        />
      </div>

      <div className="pt-4 pb-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            checked={enablePenalties}
            onChange={(e) => setEnablePenalties(e.target.checked)}
          />
          <div>
            <div className="font-semibold text-sm text-gray-900">Enable penalty rules for this plan</div>
            <div className="text-xs text-gray-500 mt-0.5">When disabled, no penalty fees are assessed. Existing rate penalties still apply where configured.</div>
          </div>
        </label>
      </div>

      {enablePenalties && (
        <SharedFormArray
          name="penalties"
          form={penaltiesForm}
          itemFields={mode === 'common' ? commonPenaltyFields : parameterWisePenaltyFields}
          variant={mode === 'common' ? 'row' : 'card'}
          gridColumns={mode === 'common' ? undefined : 12}
          footerText={
            mode === 'common'
              ? "Flat $ bills a fixed dollar amount; Percent % bills as a percent of the overdue balance. Grace delays the fee by N days after the trigger event."
              : "Each rule fires when the consumer's parameter matches. Flat $ bills a fixed dollar amount; Percent % bills the amount as a percent of the overdue balance. Grace delays the fee by N days after the trigger event."
          }
          addButtonText={mode === 'common' ? "Add Penalty" : "Add Rule"}
          emptyMessage="No penalties added."
        />
      )}

      {/* Footer Actions */}
      <div className="flex items-center justify-between mt-6 border-t pt-6">
        <div>
          {!isFirstStep && onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
        </div>
        <Button type="button" onClick={validateAndNext} disabled={isPending}>
          {isPending ? "Saving..." : (isLastStep ? "Finish" : "Next")}
        </Button>
      </div>
    </div>
  );
};

export default Penalties;
